/**
 * SAP Integration — Supabase Persistence Service
 *
 * Mirrors sapIntegrationService.ts but reads/writes to Supabase tables
 * defined in 00003_sap_integration_tables.sql. Converts snake_case DB
 * columns to camelCase for the app layer.
 */

import { supabase } from '@/lib/supabase'
import type {
  SapSystemConfig,
  SapIntegrationProfile,
  SapMappingGroup,
  FieldMapping,
  SapSyncJob,
  SyncDomain,

  SapCostLineItem,
  SapCommitmentLineItem,
  SapCostElementMaster,
  SapWbsElementMaster,
  SyncedActualsSummary,
  SapIntegrationDashboard,

} from '@/types/sap-integration'

// ---------------------------------------------------------------------------
// Helpers — reuse the same snake→camel pattern from supabaseService.ts
// ---------------------------------------------------------------------------

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
}

function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, m => `_${m.toLowerCase()}`)
}

function transformKeys(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    out[snakeToCamel(k)] = v
  }
  return out
}

function toSnakeKeys(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    out[camelToSnake(k)] = v
  }
  return out
}

function transformRow<T>(row: Record<string, unknown>): T {
  return transformKeys(row) as T
}

function transformRows<T>(rows: Record<string, unknown>[]): T[] {
  return rows.map(r => transformRow<T>(r))
}

// ---------------------------------------------------------------------------
// SAP System Connections
// ---------------------------------------------------------------------------

export async function getSapSystems(): Promise<SapSystemConfig[]> {
  const { data, error } = await supabase.from('sap_system_configs').select('*')
  if (error) throw new Error(`Supabase error [sap_system_configs]: ${error.message}`)
  return transformRows<SapSystemConfig>((data ?? []) as unknown as Record<string, unknown>[])
}

export async function getSapSystemById(id: string): Promise<SapSystemConfig | null> {
  const { data, error } = await supabase.from('sap_system_configs').select('*').eq('id', id).maybeSingle()
  if (error) throw new Error(`Supabase error [sap_system_configs/${id}]: ${error.message}`)
  if (!data) return null
  return transformRow<SapSystemConfig>(data as unknown as Record<string, unknown>)
}

export async function upsertSapSystem(config: Partial<SapSystemConfig> & { id?: string }): Promise<SapSystemConfig> {
  const row = toSnakeKeys(config as Record<string, unknown>)
  const { data, error } = await supabase
    .from('sap_system_configs')
    .upsert(row, { onConflict: 'id' })
    .select()
    .single()
  if (error) throw new Error(`Supabase upsert error [sap_system_configs]: ${error.message}`)
  return transformRow<SapSystemConfig>(data as unknown as Record<string, unknown>)
}

export async function deleteSapSystem(id: string): Promise<boolean> {
  const { error } = await supabase.from('sap_system_configs').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete error [sap_system_configs/${id}]: ${error.message}`)
  return true
}

// ---------------------------------------------------------------------------
// Integration Profiles
// ---------------------------------------------------------------------------

export async function getIntegrationProfiles(filters?: {
  projectId?: string
  portfolioId?: string
}): Promise<SapIntegrationProfile[]> {
  let q = supabase.from('sap_integration_profiles').select('*')
  if (filters?.projectId) q = q.eq('project_id', filters.projectId)
  if (filters?.portfolioId) q = q.eq('portfolio_id', filters.portfolioId)
  const { data, error } = await q
  if (error) throw new Error(`Supabase error [sap_integration_profiles]: ${error.message}`)

  const profiles = transformRows<SapIntegrationProfile>((data ?? []) as unknown as Record<string, unknown>[])

  // Hydrate mapping groups for each profile
  for (const profile of profiles) {
    profile.mappingGroups = await getMappingGroups(profile.id)
  }
  return profiles
}

export async function getIntegrationProfileById(id: string): Promise<SapIntegrationProfile | null> {
  const { data, error } = await supabase.from('sap_integration_profiles').select('*').eq('id', id).maybeSingle()
  if (error) throw new Error(`Supabase error [sap_integration_profiles/${id}]: ${error.message}`)
  if (!data) return null

  const profile = transformRow<SapIntegrationProfile>(data as unknown as Record<string, unknown>)
  profile.mappingGroups = await getMappingGroups(profile.id)
  return profile
}

export async function upsertIntegrationProfile(
  profile: Partial<SapIntegrationProfile> & { id?: string }
): Promise<SapIntegrationProfile> {
  // Separate mapping groups — they live in their own table
  const { mappingGroups: _mg, ...profileData } = profile
  const row = toSnakeKeys(profileData as Record<string, unknown>)

  const { data, error } = await supabase
    .from('sap_integration_profiles')
    .upsert(row, { onConflict: 'id' })
    .select()
    .single()
  if (error) throw new Error(`Supabase upsert error [sap_integration_profiles]: ${error.message}`)

  const saved = transformRow<SapIntegrationProfile>(data as unknown as Record<string, unknown>)
  saved.mappingGroups = await getMappingGroups(saved.id)
  return saved
}

export async function deleteIntegrationProfile(id: string): Promise<boolean> {
  const { error } = await supabase.from('sap_integration_profiles').delete().eq('id', id)
  if (error) throw new Error(`Supabase delete error [sap_integration_profiles/${id}]: ${error.message}`)
  return true
}

// ---------------------------------------------------------------------------
// Mapping Groups
// ---------------------------------------------------------------------------

export async function getMappingGroups(profileId: string): Promise<SapMappingGroup[]> {
  const { data, error } = await supabase
    .from('sap_mapping_groups')
    .select('*')
    .eq('profile_id', profileId)
    .order('sort_order')
  if (error) throw new Error(`Supabase error [sap_mapping_groups]: ${error.message}`)

  const groups = transformRows<SapMappingGroup>((data ?? []) as unknown as Record<string, unknown>[])

  // Hydrate field mappings for each group
  for (const group of groups) {
    group.fieldMappings = await getFieldMappings(group.id)
  }
  return groups
}

export async function upsertMappingGroup(
  group: Partial<SapMappingGroup> & { profileId: string; id?: string }
): Promise<SapMappingGroup> {
  const { fieldMappings: _fm, ...groupData } = group
  const row = toSnakeKeys(groupData as Record<string, unknown>)

  const { data, error } = await supabase
    .from('sap_mapping_groups')
    .upsert(row, { onConflict: 'id' })
    .select()
    .single()
  if (error) throw new Error(`Supabase upsert error [sap_mapping_groups]: ${error.message}`)

  const saved = transformRow<SapMappingGroup>(data as unknown as Record<string, unknown>)
  saved.fieldMappings = await getFieldMappings(saved.id)
  return saved
}

export async function deleteMappingGroup(groupId: string): Promise<boolean> {
  const { error } = await supabase.from('sap_mapping_groups').delete().eq('id', groupId)
  if (error) throw new Error(`Supabase delete error [sap_mapping_groups/${groupId}]: ${error.message}`)
  return true
}

// ---------------------------------------------------------------------------
// Field Mappings
// ---------------------------------------------------------------------------

export async function getFieldMappings(mappingGroupId: string): Promise<FieldMapping[]> {
  const { data, error } = await supabase
    .from('sap_field_mappings')
    .select('*')
    .eq('mapping_group_id', mappingGroupId)
    .order('sort_order')
  if (error) throw new Error(`Supabase error [sap_field_mappings]: ${error.message}`)
  return transformRows<FieldMapping>((data ?? []) as unknown as Record<string, unknown>[])
}

export async function upsertFieldMapping(
  mapping: Partial<FieldMapping> & { mappingGroupId: string; id?: string }
): Promise<FieldMapping> {
  const row = toSnakeKeys(mapping as Record<string, unknown>)

  const { data, error } = await supabase
    .from('sap_field_mappings')
    .upsert(row, { onConflict: 'id' })
    .select()
    .single()
  if (error) throw new Error(`Supabase upsert error [sap_field_mappings]: ${error.message}`)
  return transformRow<FieldMapping>(data as unknown as Record<string, unknown>)
}

export async function deleteFieldMapping(mappingId: string): Promise<boolean> {
  const { error } = await supabase.from('sap_field_mappings').delete().eq('id', mappingId)
  if (error) throw new Error(`Supabase delete error [sap_field_mappings/${mappingId}]: ${error.message}`)
  return true
}

// ---------------------------------------------------------------------------
// Sync Jobs
// ---------------------------------------------------------------------------

export async function getSyncJobs(
  profileId?: string,
  domain?: SyncDomain
): Promise<SapSyncJob[]> {
  let q = supabase.from('sap_sync_jobs').select('*').order('started_at', { ascending: false })
  if (profileId) q = q.eq('profile_id', profileId)
  if (domain) q = q.eq('domain', domain)
  const { data, error } = await q
  if (error) throw new Error(`Supabase error [sap_sync_jobs]: ${error.message}`)
  return transformRows<SapSyncJob>((data ?? []) as unknown as Record<string, unknown>[])
}

export async function getSyncJobById(id: string): Promise<SapSyncJob | null> {
  const { data, error } = await supabase.from('sap_sync_jobs').select('*').eq('id', id).maybeSingle()
  if (error) throw new Error(`Supabase error [sap_sync_jobs/${id}]: ${error.message}`)
  if (!data) return null
  return transformRow<SapSyncJob>(data as unknown as Record<string, unknown>)
}

export async function createSyncJob(job: Omit<SapSyncJob, 'id'>): Promise<SapSyncJob> {
  const row = toSnakeKeys(job as unknown as Record<string, unknown>)

  const { data, error } = await supabase
    .from('sap_sync_jobs')
    .insert(row)
    .select()
    .single()
  if (error) throw new Error(`Supabase insert error [sap_sync_jobs]: ${error.message}`)
  return transformRow<SapSyncJob>(data as unknown as Record<string, unknown>)
}

export async function updateSyncJob(id: string, updates: Partial<SapSyncJob>): Promise<SapSyncJob> {
  const row = toSnakeKeys(updates as Record<string, unknown>)

  const { data, error } = await supabase
    .from('sap_sync_jobs')
    .update(row)
    .eq('id', id)
    .select()
    .single()
  if (error) throw new Error(`Supabase update error [sap_sync_jobs/${id}]: ${error.message}`)
  return transformRow<SapSyncJob>(data as unknown as Record<string, unknown>)
}

// ---------------------------------------------------------------------------
// Cost Line Items (raw SAP CO data)
// ---------------------------------------------------------------------------

export async function insertCostLineItems(items: Omit<SapCostLineItem, 'id'>[], syncJobId: string, profileId: string): Promise<number> {
  const rows = items.map(item => ({
    ...toSnakeKeys(item as unknown as Record<string, unknown>),
    sync_job_id: syncJobId,
    profile_id: profileId,
  }))

  // Batch insert in chunks of 500
  let inserted = 0
  for (let i = 0; i < rows.length; i += 500) {
    const chunk = rows.slice(i, i + 500)
    const { error } = await supabase.from('sap_cost_line_items').insert(chunk)
    if (error) throw new Error(`Supabase insert error [sap_cost_line_items]: ${error.message}`)
    inserted += chunk.length
  }
  return inserted
}

export async function getCostLineItems(
  profileId: string,
  filters?: { fiscalYear?: string; postingPeriod?: string; valueType?: string; wbsElement?: string }
): Promise<SapCostLineItem[]> {
  let q = supabase.from('sap_cost_line_items').select('*').eq('profile_id', profileId)
  if (filters?.fiscalYear) q = q.eq('fiscal_year', filters.fiscalYear)
  if (filters?.postingPeriod) q = q.eq('posting_period', filters.postingPeriod)
  if (filters?.valueType) q = q.eq('value_type', filters.valueType)
  if (filters?.wbsElement) q = q.eq('wbs_element', filters.wbsElement)
  const { data, error } = await q
  if (error) throw new Error(`Supabase error [sap_cost_line_items]: ${error.message}`)
  return transformRows<SapCostLineItem>((data ?? []) as unknown as Record<string, unknown>[])
}

// ---------------------------------------------------------------------------
// Commitment Line Items
// ---------------------------------------------------------------------------

export async function insertCommitmentLineItems(items: Omit<SapCommitmentLineItem, 'id'>[], syncJobId: string, profileId: string): Promise<number> {
  const rows = items.map(item => ({
    ...toSnakeKeys(item as unknown as Record<string, unknown>),
    sync_job_id: syncJobId,
    profile_id: profileId,
  }))

  let inserted = 0
  for (let i = 0; i < rows.length; i += 500) {
    const chunk = rows.slice(i, i + 500)
    const { error } = await supabase.from('sap_commitment_line_items').insert(chunk)
    if (error) throw new Error(`Supabase insert error [sap_commitment_line_items]: ${error.message}`)
    inserted += chunk.length
  }
  return inserted
}

export async function getCommitmentLineItems(
  profileId: string,
  filters?: { wbsElement?: string; commitmentType?: string }
): Promise<SapCommitmentLineItem[]> {
  let q = supabase.from('sap_commitment_line_items').select('*').eq('profile_id', profileId)
  if (filters?.wbsElement) q = q.eq('wbs_element', filters.wbsElement)
  if (filters?.commitmentType) q = q.eq('commitment_type', filters.commitmentType)
  const { data, error } = await q
  if (error) throw new Error(`Supabase error [sap_commitment_line_items]: ${error.message}`)
  return transformRows<SapCommitmentLineItem>((data ?? []) as unknown as Record<string, unknown>[])
}

// ---------------------------------------------------------------------------
// Cost Element Master
// ---------------------------------------------------------------------------

export async function getCostElementMasters(profileId: string): Promise<SapCostElementMaster[]> {
  const { data, error } = await supabase
    .from('sap_cost_element_master')
    .select('*')
    .eq('profile_id', profileId)
  if (error) throw new Error(`Supabase error [sap_cost_element_master]: ${error.message}`)
  return transformRows<SapCostElementMaster>((data ?? []) as unknown as Record<string, unknown>[])
}

export async function upsertCostElementMasters(
  items: SapCostElementMaster[],
  profileId: string
): Promise<number> {
  const rows = items.map(item => ({
    ...toSnakeKeys(item as unknown as Record<string, unknown>),
    profile_id: profileId,
  }))

  const { error } = await supabase
    .from('sap_cost_element_master')
    .upsert(rows, { onConflict: 'profile_id,cost_element' })
  if (error) throw new Error(`Supabase upsert error [sap_cost_element_master]: ${error.message}`)
  return rows.length
}

// ---------------------------------------------------------------------------
// WBS Element Master
// ---------------------------------------------------------------------------

export async function getWbsElementMasters(profileId: string): Promise<SapWbsElementMaster[]> {
  const { data, error } = await supabase
    .from('sap_wbs_element_master')
    .select('*')
    .eq('profile_id', profileId)
  if (error) throw new Error(`Supabase error [sap_wbs_element_master]: ${error.message}`)
  return transformRows<SapWbsElementMaster>((data ?? []) as unknown as Record<string, unknown>[])
}

export async function upsertWbsElementMasters(
  items: SapWbsElementMaster[],
  profileId: string
): Promise<number> {
  const rows = items.map(item => ({
    ...toSnakeKeys(item as unknown as Record<string, unknown>),
    profile_id: profileId,
  }))

  const { error } = await supabase
    .from('sap_wbs_element_master')
    .upsert(rows, { onConflict: 'profile_id,wbs_element' })
  if (error) throw new Error(`Supabase upsert error [sap_wbs_element_master]: ${error.message}`)
  return rows.length
}

// ---------------------------------------------------------------------------
// Synced Actuals Summary (aggregated by WBS + period)
// ---------------------------------------------------------------------------

export async function getSyncedActualsSummaries(
  projectId: string,
  period?: string
): Promise<SyncedActualsSummary[]> {
  let q = supabase
    .from('sap_synced_actuals_summary')
    .select('*')
    .eq('project_id', projectId)
    .order('period')
  if (period) q = q.eq('period', period)
  const { data, error } = await q
  if (error) throw new Error(`Supabase error [sap_synced_actuals_summary]: ${error.message}`)
  return transformRows<SyncedActualsSummary>((data ?? []) as unknown as Record<string, unknown>[])
}

export async function upsertSyncedActualsSummaries(
  summaries: SyncedActualsSummary[]
): Promise<number> {
  const rows = summaries.map(s => toSnakeKeys(s as unknown as Record<string, unknown>))

  const { error } = await supabase
    .from('sap_synced_actuals_summary')
    .upsert(rows, { onConflict: 'project_id,wbs_id,period' })
  if (error) throw new Error(`Supabase upsert error [sap_synced_actuals_summary]: ${error.message}`)
  return rows.length
}

// ---------------------------------------------------------------------------
// Dashboard — aggregated view across all sync domains
// ---------------------------------------------------------------------------

export async function getIntegrationDashboard(profileId: string): Promise<SapIntegrationDashboard | null> {
  const profile = await getIntegrationProfileById(profileId)
  if (!profile) return null
  const system = await getSapSystemById(profile.sapSystemId)
  if (!system) return null
  const jobs = await getSyncJobs(profileId)

  const domainKeys: SyncDomain[] = [
    'CostActuals', 'Commitments', 'WbsStructure', 'Budget',
    'Forecast', 'LaborTime', 'MasterData', 'Revenue',
  ]
  const lastSyncByDomain = Object.fromEntries(
    domainKeys.map(d => [d, jobs.find(j => j.domain === d) ?? null])
  ) as Record<SyncDomain, SapSyncJob | null>

  const totalFields = profile.mappingGroups.reduce((sum, g) => sum + g.fieldMappings.length, 0)
  const mappedFields = profile.mappingGroups.reduce(
    (sum, g) => sum + g.fieldMappings.filter(f => f.enabled).length, 0
  )

  return {
    profileId: profile.id,
    profileName: profile.name,
    sapSystemName: system.name,
    connectionStatus: system.status,
    lastSyncByDomain,
    totalRecordsSynced: jobs.reduce((sum, j) => sum + j.recordsCreated + j.recordsUpdated, 0),
    totalErrors: jobs.reduce((sum, j) => sum + j.recordsErrored, 0),
    nextScheduledSync: null,
    mappingCoverage: { totalFields, mappedFields, unmappedFields: totalFields - mappedFields },
  }
}
